import { useRef } from "react";

/**
 * Structural equality for form-state snapshots. Handles the value shapes the
 * useState-based creation forms hold: primitives, arrays, plain objects, Dates,
 * and Firebase Timestamps (anything exposing `toMillis()`).
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  // Date / Firebase Timestamp — compare by their millisecond value.
  const aMillis = toMillis(a);
  const bMillis = toMillis(b);
  if (aMillis !== null || bMillis !== null) {
    return aMillis === bMillis;
  }

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!aIsArray || !bIsArray || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => valuesEqual(item, b[i]));
  }

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(bObj, key) && valuesEqual(aObj[key], bObj[key])
  );
}

function toMillis(value: object): number | null {
  if (value instanceof Date) {
    return value.getTime();
  }
  const maybeTimestamp = value as { toMillis?: () => number };
  if (typeof maybeTimestamp.toMillis === "function") {
    return maybeTimestamp.toMillis();
  }
  return null;
}

/**
 * Returns whether `value` differs from the value it held on first render.
 *
 * Designed for the useState-based creation forms (New Trial / New Experiment),
 * whose form components mount fresh each time their dialog opens — so the first
 * render captures the pristine baseline. Compared structurally, so editing a
 * field and then restoring it reports clean again.
 */
export function useDirtySnapshot<T>(value: T): boolean {
  const initialRef = useRef<T>(value);
  return !valuesEqual(value, initialRef.current);
}
